import React, { useState, useEffect } from 'react';
import API_BASE_URL from"../Config/config";
import axios from 'axios';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Box,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CardActions,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';

function RecruiterRequests() {
  const [adminRequests, setAdminRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await axios.get('/api/super-admin/admin-requests');
        setAdminRequests(response.data);
      } catch (error) {
        console.error('Failed to fetch admin requests:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleAction = async (id, status) => {
    try {
      await axios.patch(`/api/super-admin/admin-requests/${id}`, {
        status,
        adminFeedback: feedback,
      });
      // Refresh list
      const res = await axios.get('/api/super-admin/admin-requests');
      setAdminRequests(res.data);
      setOpenDialog(false);
      setFeedback("");
    } catch (err) {
      console.error("Failed to update request:", err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>


      <Typography variant="h4" gutterBottom sx={{ color: '#4F46E5', fontWeight: 'bold' }}>
        Recruiter Requests
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage and review requests from users who want to register as Hiring Managers.
      </Typography>

      <Grid container spacing={3}>
        {adminRequests.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No requests found</Typography>
            </Paper>
          </Grid>
        )}
        {adminRequests.map((req) => (
          <Grid item xs={12} sm={6} md={4} key={req.id}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderLeft: `6px solid ${
                  req.status === 'approved' ? '#4caf50' : 
                  req.status === 'rejected' ? '#f44336' : '#ff9800'
                }`
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#4F46E5', fontWeight: 'bold' }}>
                    {req.company_name}
                  </Typography>
                  <Chip 
                    label={req.status.toUpperCase()} 
                    color={
                      req.status === 'approved' ? 'success' : 
                      req.status === 'rejected' ? 'error' : 'warning'
                    }
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>CEO:</strong> {req.company_ceo_name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Specialization:</strong> {req.company_specialization || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Phone:</strong> {req.contact_phone}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Website:</strong> {req.company_website || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: '0.8rem' }}>
                  Submitted: {new Date(req.created_at).toLocaleDateString()}
                </Typography>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                {req.status === 'pending' ? (
                  <Button 
                    fullWidth 
                    variant="contained" 
                    onClick={() => { setSelectedRequest(req); setOpenDialog(true); }}
                  >
                    Review Request
                  </Button>
                ) : (
                  <Button 
                    fullWidth 
                    variant="outlined" 
                    onClick={() => { setSelectedRequest(req); setOpenDialog(true); }}
                  >
                    View Details
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#4F46E5', fontWeight: 'bold' }}>
          {selectedRequest?.company_name} - Request Details
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">CEO Name</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.company_ceo_name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Contact Phone</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.contact_phone}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Company Address</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.company_address}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Specialization</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.company_specialization || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Website</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.company_website || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Business Registration Number</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.business_registration_number || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Board Members</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.company_board_members || 'N/A'}</Typography>
                </Grid>
                {selectedRequest.documents && selectedRequest.documents.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Uploaded Documents</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {selectedRequest.documents.map((doc, index) => (
                        <Button
                          key={index}
                          variant="outlined"
                          size="small"
                          component="a"
                          href={`${API_BASE_URL}${doc.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: 'none' }}
                        >
                          {doc.name}
                        </Button>
                      ))}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={selectedRequest.status.toUpperCase()} 
                    color={
                      selectedRequest.status === 'approved' ? 'success' : 
                      selectedRequest.status === 'rejected' ? 'error' : 'warning'
                    }
                    size="small"
                  />
                </Grid>
              </Grid>
              
              <TextField
                fullWidth
                label="Feedback / Reason"
                sx={{ mt: 3 }}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                multiline
                rows={3}
                placeholder="Provide feedback for the recruiter..."
                defaultValue={selectedRequest.admin_feedback || ""}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)} color="inherit">Close</Button>
          {selectedRequest?.status === 'pending' && (
            <>
              <Button color="error" variant="outlined" onClick={() => handleAction(selectedRequest.id, 'rejected')}>Reject Request</Button>
              <Button color="success" variant="contained" onClick={() => handleAction(selectedRequest.id, 'approved')}>Approve Request</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default RecruiterRequests;
